import {
    useJWT
} from "./JWT";
import { HelpoverInputGroup } from './Helpover';
import React from "react";
import {
    Link
} from "react-router-dom";
import { RefreshButton } from "./RefreshButton";
import { CreateButton } from "./CreateButton";
import { DeleteButton } from "./DeleteButton";
import {
    BiLoaderCircle,
    BiWrench
} from "react-icons/bi";
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import ButtonToolbar from 'react-bootstrap/ButtonToolbar';
import Table from 'react-bootstrap/Table';
import Modal from 'react-bootstrap/Modal';

function SetupBot(props) {
    let jwt = useJWT();
    const [error, setError] = React.useState("");
    const [working, setWorking] = React.useState(false);
    const [handle, setHandle] = React.useState("");
    const [discord, setDiscord] = React.useState(false);
    const [secrets, setSecrets] = React.useState({});

    async function handleSetup(jwt, event) {
        event.preventDefault();

        setWorking(true);
        fetch(`https://api.${process.env.REACT_APP_DOMAIN}/rest/bots/${jwt.claims.namespace}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + jwt.token
            },
            body: JSON.stringify({
                'handle': handle,
                'secrets': secrets
            })
        }).then(response => {
            if (!response.ok) {
                throw new Error(`${response.status} ${response.statusText}`);
            }
            return response
        }).then(payload => {
            setError("");
            setWorking(false);
            props.onComplete();
        }).catch(error => {
            console.error("setting up bot: ", error);
            setError(error);
            setWorking(false);
        });
    }

    return (
        <>
          {error &&
             (<Alert variant="danger">
                {error.message}
              </Alert>)}
          <Alert variant="info">
            This step just prepares a repository for you to <code>docker push</code> your images. The billing clock won't start ticking until it's actually running.
          </Alert>
          <Form onSubmit={(event) => handleSetup(jwt, event)}>
            <Form.Row>
              <InputGroup className="mb-3">
                <InputGroup.Prepend>
                  <InputGroup.Text>{jwt.claims.namespace}/</InputGroup.Text>
                </InputGroup.Prepend>
                <Form.Control placeholder="handle" onChange={(e) => setHandle(e.target.value)} />
                <HelpoverInputGroup topic="handle"/>
              </InputGroup>
            </Form.Row>
            <Form.Row hidden={discord}>
              <Button variant="link" size="sm" onClick={(e) => setDiscord(true)}>
                add discord bot details
              </Button>
            </Form.Row>
            <Form.Row hidden={!discord}>
                <InputGroup>
                  <Form.Control placeholder="discord client secret" onChange={(e) => setSecrets({'discord_client_secret': e.target.value})} />
                  <HelpoverInputGroup topic="discord client secret"/>
                </InputGroup>
                <Button variant="link" size="sm" onClick={(e) => { setDiscord(false); setSecrets({}); }}>
                  never mind, don't add discord bot details (you can always add them later!)
                </Button>
            </Form.Row>
            <Form.Row>
              <Button variant="warning" block size="sm" type="submit" disabled={working}>
                setup bot
              </Button>
            </Form.Row>
          </Form>
        </>
    );
}

function EradicateBot(props) {
    let jwt = useJWT();
    const [error, setError] = React.useState("");
    const [working, setWorking] = React.useState(false);

    async function handleEradicate(jwt, event) {
        event.preventDefault();

        setWorking(true);
        fetch(`https://api.${process.env.REACT_APP_DOMAIN}/rest/bots/${props.fqbn}`, {
            method: 'DELETE',
            headers: {
                'Authorization': 'Bearer ' + jwt.token
            }
        }).then(response => {
            if (!response.ok) {
                throw new Error(`${response.status} ${response.statusText}`);
            }
            return response
        }).then(response => {
            setError("");
            setWorking(false);
            props.onComplete();
        }).catch(error => {
            console.error("removing secret: ", error);
            setError(error);
            setWorking(false);
        });
    }

    return (
        <>
          {error &&
             (<Alert variant="danger">
                {error.message}
              </Alert>)}
          <Alert variant="warning">
            Are you super-duper sure you want to eradicate <span className="font-weight-bold">{props.fqbn}</span>? Once it's gone, it's gone!
          </Alert>
          <Form onSubmit={(event) => handleEradicate(jwt, event)}>
            <Form.Row>
              <Button variant="danger" block size="sm" type="submit" disabled={working}>
                eradicate <span className="font-weight-bold">{props.fqbn}</span>
              </Button>
            </Form.Row>
          </Form>
        </>
    );
}

function Dashboard() {
    let jwt = useJWT();
    const [error, setError] = React.useState("");
    const [loading, setLoading] = React.useState(true);
    const [bots, setBots] = React.useState([]);
    const [settingup, setSettingup] = React.useState(false);
    const [eradicating, setEradicating] = React.useState("");

    async function loadBots(jwt) {
        fetch(`https://api.${process.env.REACT_APP_DOMAIN}/rest/accounts/${jwt.claims.sub}`, {
            method: 'GET',
            headers: {
                'Authorization': 'Bearer ' + jwt.token
            }
        }).then(response => {
            if (!response.ok) {
                throw new Error(`${response.status} ${response.statusText}`);
            }
            return response.json()
        }).then(payload => {
            setBots(payload.bots);
            setError(false);
            setLoading(false);
        }).catch(error => {
            console.error("loading user: ", error);
            setBots([]);
            setError(error);
            setLoading(false);
        });
    }

    React.useEffect(() => {
        loadBots(jwt);
    }, [jwt]);

    if (loading) {
        return (
          <><BiLoaderCircle/></>
        );
    }
    if (error) {
        return (
          <Alert variant="danger">
            {error.message}
          </Alert>
        );
    }
    return (
      <>
        <Modal size="lg" show={settingup} onHide={() => setSettingup(false)}>
          <Modal.Header closeButton>
            <Modal.Title>setup a new bot!</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <SetupBot onComplete={() => {setSettingup(false); loadBots(jwt)}}/>
          </Modal.Body>
        </Modal>

        <Modal size="lg" show={eradicating !== ""} onHide={() => setEradicating("")}>
          <Modal.Header closeButton>
          </Modal.Header>
          <Modal.Body>
            <EradicateBot fqbn={eradicating} onComplete={() => {setEradicating(""); loadBots(jwt)}}/>
          </Modal.Body>
        </Modal>

        <ButtonToolbar className="justify-content-end mb-1">
          <RefreshButton className="mr-1" onClick={() => loadBots(jwt)}/>
          <CreateButton onClick={() => setSettingup(true)}>setup bot</CreateButton>
        </ButtonToolbar>

        <Table hover size="sm">
        <thead>
          <tr>
            <th>bot</th>
            <th>status</th>
            <th></th>
          </tr>
        </thead>

        <tbody>
        {bots.map(bot => (
          <tr key={`${bot.namespace}/${bot.handle}`}>
            <td>
              <Link to={`/${bot.namespace}/${bot.handle}`}>
                <BiWrench/><span className="font-weight-bold">{bot.namespace}/{bot.handle}</span>
              </Link>
            </td>
            <td>
            { bot.image_digest
                ? ( bot.deployment_digest
                    ? ( bot.deployment_available
                        ? (
                            <Alert variant="success" className="mb-0 p-1 small text-nowrap">
                              running
                            </Alert>)
                        : ( bot.deployment_progressing
                            ? (
                                <Alert variant="info" className="mb-0 p-1 small text-nowrap">
                                  deploying
                                </Alert>)
                            : (
                                <Alert variant="danger" className="mb-0 p-1 small text-nowrap">
                                  deployment failed! check your <Link to={`${bot.namespace}/${bot.handle}/logs`}>logs</Link>, or <Link to="mailto:support@allthebots.dev">contact support</Link>
                                </Alert>
                            )))
                    : (
                        <Alert variant="info" className="mb-0 p-1 small text-nowrap">
                          waiting for deployment
                        </Alert>))
                : (
                    <Alert variant="light" className="mb-0 p-1 small text-nowrap">
                      next step: <code>docker push registry.{process.env.REACT_APP_DOMAIN}/{bot.namespace}/{bot.handle}:latest</code>
                    </Alert>
                )}
            </td>
            <td>
              <ButtonToolbar className="justify-content-end">
                <DeleteButton className="text-nowrap" onClick={() => setEradicating(`${bot.namespace}/${bot.handle}`)}>eradicate bot</DeleteButton>
              </ButtonToolbar>
            </td>
          </tr>
        ))}
        </tbody>
        </Table>

        {(bots.length === 0) &&
            (
                <Alert variant="info">
                what? no bots yet? hit the <span className="font-weight-bold">setup bot</span> button!
                </Alert>
            )}
      </>
    );
}

export default Dashboard;
